/* eslint-disable camelcase */
import { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { results, PlainTable, InternalLink } from '@mdc/ui';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import { FileResultsService } from '@mdc/services';
import { OS_NAME } from '@mdc/constants';

import './BinaryReputation.scss';

const NOTHING_TO_DISPLAY = '-';

const { PlainTablePlaceholder } = results;

const BinaryReputation = ({ results, resultsLoading, scanning, appInfoResults }) => {
    const { t, ready } = useTranslation();

    const BinaryReputationData = { applications: [
        {
            Header: t('Source'),
            accessor: 'name'
        },
        {
            Header: t('Vendor'),
            accessor: 'vendor'
        },
        {
            Header: t('Version'),
            accessor: 'version'
        },
        {
            Header: t('OS'),
            accessor: 'os'
        },
        {
            Header: t('Category'),
            accessor: 'category'
        },
        {
            Header: t('Usage'),
            accessor: 'usage'
        },
        {
            Header: '',
            accessor: 'meta'
        }
    ],
    os_details: [
        {
            Header: t('Version'),
            accessor: 'versionOs'
        },
        {
            Header: t('Kernel Version'),
            accessor: 'kernel'
        },
        {
            Header: t('Service Packs'),
            accessor: 'service'
        },
        {
            Header: t('Architecture'),
            accessor: 'architecture'
        },
        {
            Header: t('Languages'),
            accessor: 'languages'
        }
    ],
    file_details: [
        {
            Header: t('Name'),
            accessor: 'name'
        },
        {
            Header: t('File Version'),
            accessor: 'fileVersion'
        },
        {
            Header: t('Full Path'),
            accessor: 'fullPaths'
        }
    ],
    components: [
        {
            Header: t('Component'),
            accessor: 'component'
        },
        {
            Header: t('Threats Detected'),
            accessor: 'threat'
        },
        {
            Header: t('Popularity'),
            accessor: 'stat'
        },
        {
            Header: t('Component Count'),
            accessor: 'count'
        },
        {
            Header: t('Component Rank'),
            accessor: 'rank'
        }
    ],
    files: [
        {
            Header: t('Name'),
            accessor: 'name'
        },
        {
            Header: t('Path'),
            accessor: 'path'
        },
        {
            Header: t('Popularity'),
            accessor: 'popularity'
        }
    ]
    };
    const appinfo = useMemo(() => appInfoResults?.appinfo, [appInfoResults]);

    const loadedComponentStats = useMemo(() => {
        if (appinfo) {
            return appinfo.filter((app) => app?.loaded_component_stats?.length).reduce((accumulated, app) => [...accumulated, ...app.loaded_component_stats], []);
        }
    }, [appinfo]);
    const totalComponents = useMemo(() => loadedComponentStats ? loadedComponentStats.reduce((total, stat) => total + stat.loaded_component_count, 0) : 1, [loadedComponentStats]);

    const [hasMoreComponentData, setHasMoreComponentData] = useState(false);
    const [componentsTableData, setComponentsTableData] = useState(undefined);


    const applicationListDom = <div className='infoSection applicationSection'>
        <h6 className='title'>{t('Application List')}</h6>
        <p>
            {t('Click to view the operating systems,  kernel version, service pack, system architecture and OS language')}
        </p>
    </div>;

    const loadedComponentDom = <div className='infoSection'>
        <h6 className='title'>{t('Loaded Components')}</h6>
        <p>
            {t('Listed and ranked by the frequency at which the component was reported for the selected application')}
        </p>
    </div>;

    const filesPathDom = <div className='infoSection'>
        <h6 className='title'>{t('Files')}</h6>
        <p>
            {t('The same hash can be reported with multiple file names. We display a list of file paths as well as the usage percentage for each path. Paths are adjusted for privacy')}
        </p>
    </div>;

    const applicationData = useMemo(() => {
        if (!appinfo) {
            return undefined;
        }

        let products;

        if (appInfoResults?.source === 'nist') {
            products = appinfo.map((app) => app.product_info.map((prod) =>
                ({
                    ...prod,
                    category: app.category,
                    os_infos: prod.os_info.map((os) => ({ ...os, language: os.language ? os.language : prod.language })),
                    vendor_infos: prod.vendor_info
                })))
                .reduce((accumulated, prods) => [...accumulated, ...prods], []);
        } else {
            const getProductData = (appData, index) => {
                if (appData?.length > index) {
                    return appData[index];
                }

                return appData?.length ? appData[0] : undefined;
            };
            products = appinfo.map((app) => app.product_infos.map((prod, index) =>
                ({
                    ...prod,
                    categories: app.categories,
                    category: app.category,
                    os_infos: app.os_infos,
                    file_info: getProductData(app.file_infos, index),
                    file_path_stats: app.file_path_stats,
                    product_info_stat: getProductData(app.product_info_stats, index),
                    vendor_infos: app.vendor_infos
                })))
                .reduce((accumulated, prods) => [...accumulated, ...prods], []);
        }

        const totalUsage = products.reduce((sum, prod) => sum + (prod.product_info_stat ? prod.product_info_stat.product_name_norm_count : 0), 0);

        return products.map((prod) => {
            const applicationData = {
                name: prod.product_name || NOTHING_TO_DISPLAY,
                version: prod.product_version || NOTHING_TO_DISPLAY,
                category: NOTHING_TO_DISPLAY,
                vendor: NOTHING_TO_DISPLAY,
                allOs: [],
                os: NOTHING_TO_DISPLAY,
                osDetails: []
            };

            // OS
            if (prod.os_infos) {
                prod.os_infos.forEach((osInfo) => {

                    const osName = osInfo.os_name.toLowerCase();
                    const osArch = osInfo.arch?.replace(/\D/g, '');
                    const osLanguage = osInfo.language?.replace(/\(.*\)/g, '').trim();
                    let selectedOs;
                    let osVersion;

                    // Establish major OS
                    switch (true) {
                        case osName.indexOf('microsoft') > -1:
                        case osName.indexOf('windows') > -1:
                            if (!applicationData.allOs.some((os) => os.name === OS_NAME.microsoft)) {
                                applicationData.allOs.push({
                                    name: OS_NAME.microsoft,
                                    icon: 'icon-windows',
                                    versions: []
                                });
                            }

                            osVersion = osName.match(/\b(7|8\.1|8|10|2000|vista|xp|me|nt)\b/g);
                            if (!osVersion) {
                                osVersion = ' ';
                            } else {
                                osVersion = osVersion[0];
                            }
                            selectedOs = applicationData.allOs.filter((os) => os.name === OS_NAME.microsoft)[0];
                            break;

                        case osName.indexOf('mac') > -1:
                        case osName.indexOf('osx') > -1:
                            if (!applicationData.allOs.some((os) => os.name === OS_NAME.osx)) {
                                applicationData.allOs.push({
                                    name: OS_NAME.osx,
                                    icon: 'icon-apple',
                                    versions: []
                                });
                            }

                            osVersion = osName.replace(OS_NAME.osx.toLowerCase(), '').trim();
                            selectedOs = applicationData.allOs.filter((os) => os.name === OS_NAME.osx)[0];
                            break;

                        case osName.indexOf('linux') > -1:
                        case osName.indexOf('ubuntu') > -1:
                        case osName.indexOf('debian') > -1:
                        case osName.indexOf('unix') > -1:
                        case osName.indexOf('redhat') > -1:
                        case osName.indexOf('red hat') > -1:
                            if (!applicationData.allOs.some((os) => os.name === OS_NAME.linux)) {
                                applicationData.allOs.push({
                                    name: OS_NAME.linux,
                                    icon: 'icon-linux',
                                    versions: []
                                });
                            }

                            osVersion = osName.replace(OS_NAME.linux.toLowerCase(), '').trim();
                            selectedOs = applicationData.allOs.filter((os) => os.name === OS_NAME.linux)[0];
                            break;

                        case osName.indexOf('ios') > -1:
                            if (!applicationData.allOs.some((os) => os.name === OS_NAME.ios)) {
                                applicationData.allOs.push({
                                    name: OS_NAME.ios,
                                    icon: 'icon-apple',
                                    versions: []
                                });
                            }

                            osVersion = osName.substring(osName.indexOf(OS_NAME.ios.toLowerCase()) + OS_NAME.ios.length).trim();
                            selectedOs = applicationData.allOs.filter((os) => os.name === OS_NAME.ios)[0];
                            break;

                        case osName.indexOf('solaris') > -1:
                        case osName.indexOf('sparc') > -1:
                            if (!applicationData.allOs.some((os) => os.name === OS_NAME.solaris)) {
                                applicationData.allOs.push({
                                    name: OS_NAME.solaris,
                                    versions: []
                                });
                            }

                            osVersion = osName.replace(OS_NAME.solaris.toLowerCase(), '').trim();
                            selectedOs = applicationData.allOs.filter((os) => os.name === OS_NAME.solaris)[0];
                            break;

                        default:
                            if (!applicationData.allOs.some((os) => os.name === OS_NAME.other)) {
                                applicationData.allOs.push({
                                    name: OS_NAME.other,
                                    versions: []
                                });
                            }

                            osVersion = osInfo.os_name;
                            selectedOs = applicationData.allOs.filter((os) => os.name === OS_NAME.other)[0];
                            break;
                    }

                    // Categorize OS
                    selectedOs.display = <p key={selectedOs.name}>{selectedOs.name}</p>;

                    if (!selectedOs.versions.some((version) => version.name === osVersion)) {
                        selectedOs.versions.push({
                            osName: selectedOs.name,
                            osIcon: selectedOs.icon,
                            name: osVersion,
                            languages: [],
                            architectures: [],
                            servicePacks: [],
                            kernelVersions: []
                        });
                    }

                    osVersion = selectedOs.versions.filter((version) => version.name === osVersion)[0];

                    if (osLanguage && !osVersion?.languages?.includes(osLanguage)) {
                        osVersion.languages.push(osLanguage);
                    }
                    if (osArch && !osVersion?.architectures?.includes(osArch)) {
                        osVersion.architectures.push(osArch);
                    }
                    if (osInfo.kernel_version && !osVersion?.kernelVersions?.includes(osInfo.kernel_version)) {
                        osVersion.kernelVersions.push(osInfo.kernel_version);
                    }
                    if (osInfo.service_pack && !osVersion?.servicePacks?.includes(osInfo.service_pack)) {
                        osVersion.servicePacks.push(osInfo.service_pack);
                    }
                });
            }

            // OS Details
            if (applicationData.allOs.length) {
                applicationData.os = applicationData.allOs.map((os) => os.display);
                applicationData.osDetails = applicationData.allOs.reduce((all, os) => ([...all, ...os.versions]), [])
                    .map((version) => ({
                        osName: version.osName,
                        name: version.name,
                        versionOs: (<p key={version.osName}>
                            <i className={version.osIcon}></i> <span>{`${version.osName} ${version.name}`}</span>
                        </p>),
                        kernel: version.kernelVersions.length ? version.kernelVersions.join(', ') : NOTHING_TO_DISPLAY,
                        service: version.servicePacks.length ? version.servicePacks.join(', ') : NOTHING_TO_DISPLAY,
                        architecture: version.architectures.length ? version.architectures.join(', ') : NOTHING_TO_DISPLAY,
                        languages: version.languages.length ? version.languages.join(', ') : NOTHING_TO_DISPLAY
                    }));
            }

            // Category
            if (prod.category) {
                applicationData.category = prod.category;
            } else if (prod.categories?.length) {
                applicationData.category = prod.categories.map((category) => category.category_name)
                    .filter((name, index, array) => array.indexOf(name) === index).join(', ');
            }

            //Vendor
            if (prod.vendor_infos?.length) {
                applicationData.vendor = prod.vendor_infos.map((vendor) => vendor.vendor_name)
                    .filter((name, index, array) => array.indexOf(name) === index).join(', ');
            }

            // File Info
            if (prod.file_info) {
                applicationData.file = {
                    name: prod.file_info.file_name,
                    fullPaths: prod.file_path_stats ? prod.file_path_stats
                        .map((file) => file.file_path)
                        .filter((path) => path.toLowerCase().endsWith(prod.file_info.file_name?.toLowerCase()))
                        .map((path) => (<p key={path}>{path}</p>)) : NOTHING_TO_DISPLAY,
                    fileVersion: prod.file_info.file_property_version || NOTHING_TO_DISPLAY,
                    size: prod.file_info.file_size
                };
            }

            // Usage
            const usagePercentage = `${prod.product_info_stat ? Math.round(prod.product_info_stat.product_name_norm_count / totalUsage * 100 * 100) / 100 : 0}%`;
            applicationData.usage = prod.product_info_stat !== undefined ? usagePercentage : NOTHING_TO_DISPLAY;

            return applicationData;
        });

    }, [appinfo, appInfoResults]);

    const applicationTableData = useMemo(() => {
        if (applicationData) {
            return applicationData.map((app) => (
                {
                    ...app,
                    meta: {
                        expandable: true,
                        data: {
                            custom: (
                                <>
                                    <tr className='expandedCellWrapper'>
                                        <td colSpan='100%' className='expandedCell'>
                                            <h6>{t('OS Details')}</h6>
                                            <table>
                                                <thead>
                                                    <tr>
                                                        {BinaryReputationData.os_details.map((column) => (<th key={column.Header} className={`cell-${column.accessor}`}> {t(column.Header)}</th>))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {app.osDetails.map((version) =>
                                                        (<tr key={`${version.osName} ${version.name}`}>
                                                            {BinaryReputationData.os_details.map((column, index) => (
                                                                <Fragment key={index}>
                                                                    <td className='tdCell cell-header'>{t(column.Header)}</td>
                                                                    <td
                                                                        className={`tdCell cell-${column.accessor} ${(column.accessor === 'kernel' || column.accessor === 'languages') && version[column.accessor].split(',').length > 1 ? 'wrapCell' : ''}`}
                                                                        title={(column.accessor === 'kernel' || column.accessor === 'languages') && version[column.accessor].split(',').length > 1 ? version[column.accessor] : ''}>
                                                                        {version[column.accessor]}
                                                                    </td>
                                                                </Fragment>
                                                            ))}
                                                        </tr>)
                                                    )}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                    {app.file && <tr className='expandedCellWrapper'>
                                        <td colSpan='100%' className='expandedCell'>
                                            <h6>{t('File Details')}</h6>
                                            <table>
                                                <tbody>
                                                    {BinaryReputationData.file_details.map((column) => (
                                                        <tr key={column.accessor}>
                                                            <td className='tdHeader'>{t(column.Header)}</td>
                                                            <td className={`tdCell cell-${column.accessor}`}>{app.file[column.accessor]}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>}
                                </>
                            )
                        }
                    }
                }));
        }
    }, [applicationData, t]);

    const filesTableData = useMemo(() => {
        if (appinfo?.length) {

            let filePaths = [];
            let sumCount = 0;

            appinfo.filter((app) => app.file_path_stats?.length).reduce((sum, app) => [...sum, ...app.file_path_stats], [])
                .forEach((file) => {
                    const name = file.file_path.replace(/.*\\/g, '');
                    sumCount += file.file_path_count;
                    if (name) {
                        const nameLower = name.toLowerCase();
                        const filePath = {
                            nameLower,
                            name,
                            path: file.file_path,
                            popularity: file.file_path_count
                        };
                        if (filePaths.some((f) => f.nameLower === nameLower)) {
                            filePath.name = '';
                        }

                        filePaths.push(filePath);
                    }
                });

            filePaths.forEach((file) => file.popularity = `${Math.round(file.popularity * 100 / sumCount * 100) / 100} %`);

            return filePaths;
        }
    }, [appinfo, appInfoResults]);

    const applicationTable = useMemo(() => {
        if (!applicationTableData || !appInfoResults || !results || resultsLoading || scanning) {
            return <PlainTablePlaceholder numberOfColumns={6} numberOfRows={10}/>;
        }

        return <PlainTable
            columnsData={BinaryReputationData.applications}
            data={applicationTableData}
            hasCollapseAll={true}
        />;

    }, [applicationTableData, appInfoResults, results, resultsLoading, scanning]);

    const filesTable = useMemo(() => {
        if (!filesTableData || !appInfoResults || !results || resultsLoading || scanning) {
            return <PlainTablePlaceholder numberOfColumns={3} numberOfRows={10}/>;
        }

        if (filesTableData.length === 0) {
            return null;
        }

        return <PlainTable
            columnsData={BinaryReputationData.files}
            data={filesTableData}
        />;

    }, [filesTableData, appInfoResults, results, resultsLoading, scanning]);


    const loadComponentsTableData = async (stats, totalComp, offset, limit = 5) => {
        if (stats?.length > offset) {
            const end = offset + limit < stats.length ? offset + limit : stats.length;

            const slice = stats.slice(offset, end);
            const data = [];

            for (let i = 0; i < slice.length; i++) {
                const item = slice[i];
                const result = await FileResultsService.getLoadedComponent(item.loaded_component);
                if (result) {
                    const componentClassName = result.total_detected_avs > 0 ? 'detected' : '';
                    data.push({
                        component: result ? <span className={componentClassName}> {result.display_name} </span> : NOTHING_TO_DISPLAY,
                        threat: result ?
                            (<span>
                                <span className={componentClassName}>{result.total_detected_avs}</span> / {result.total_avs}
                                <InternalLink className="link" to={`/results/file/${item.loaded_component}/hash/overview`}>{t('View results')}</InternalLink>
                            </span>)
                            : NOTHING_TO_DISPLAY,
                        stat: `${Math.round((item.loaded_component_count / totalComp) * 100 * 100) / 100}%` || NOTHING_TO_DISPLAY,
                        count: item.loaded_component_count,
                        rank: item.loaded_component_rank_over_sha1 || NOTHING_TO_DISPLAY
                    });
                }
            }

            return [data, end < stats.length];
        }

        return [[], false];
    };

    useEffect(() => {
        if (loadedComponentStats) {
            setComponentsTableData(undefined);
            (async () => {
                const [data, hasMore] = await loadComponentsTableData(loadedComponentStats, totalComponents, 0);
                setComponentsTableData(data);
                setHasMoreComponentData(hasMore);
            })();
        } else {
            setComponentsTableData([]);
            setHasMoreComponentData(false);
        }
    }, [loadedComponentStats, totalComponents, t]);

    const loadMoreComponents = useMemo(() => async () => {
        const [data, hasMore] = await loadComponentsTableData(loadedComponentStats, totalComponents, componentsTableData.length);
        if (componentsTableData) {
            setComponentsTableData([...componentsTableData, ...data]);
        } else {
            setComponentsTableData(data);
        }

        setHasMoreComponentData(hasMore);
        return data.length;
    }, [loadedComponentStats, totalComponents, componentsTableData, t]);

    const componentsTable = useMemo(() => {
        if (!componentsTableData || !appInfoResults || !results || resultsLoading || scanning) {
            return <PlainTablePlaceholder numberOfRows={10}/>;
        }

        if (componentsTableData.length === 0) {
            return null;
        }

        return <PlainTable
            columnsData={BinaryReputationData.components}
            data={componentsTableData}
            hasLoadMore={hasMoreComponentData}
            loadMoreAction={loadMoreComponents}
        />;

    }, [componentsTableData, appInfoResults, results, resultsLoading, scanning, hasMoreComponentData, loadMoreComponents]);


    if (!ready) {
        return null;
    }

    return (
        <div className='binaryReputation'>
            <Row className="rowSection applicationList">
                <Col lg={3} md={3} xs={12}>
                    {applicationListDom}
                </Col>
                <Col lg={9} md={9} xs={12}>
                    {applicationTable}
                </Col>
            </Row>
            {componentsTable && <Row className="rowSection">
                <Col lg={3} md={3} xs={12}>
                    {loadedComponentDom}
                </Col>
                <Col lg={9} md={9} xs={12}>
                    {componentsTable}
                </Col>
            </Row>}
            {filesTable && <Row>
                <Col>
                    {filesPathDom}
                </Col>
                <Col lg={9} md={9} xs={12}>
                    {filesTable}
                </Col>
            </Row>}
        </div>
    );
};

BinaryReputation.propTypes = {
    results: PropTypes.object,
    resultsLoading: PropTypes.bool,
    appInfoResults: PropTypes.object,
    scanning: PropTypes.string
};

export default BinaryReputation;
